import React, { useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { ThemeMode } from '../types/theme';
import { apiService } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { ThemeContext } from './ThemeContext';

const LOCAL_STORAGE_KEY = 'customer_portal_theme';

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, email } = useAuth();
  const [theme, setThemeState] = useState<ThemeMode>('system');
  const [loading, setLoading] = useState(true);

  // System preference media query
  const media = window.matchMedia('(prefers-color-scheme: dark)');

  // Get system preference
  const getSystemPreference = useCallback((): 'light' | 'dark' => {
    return media.matches ? 'dark' : 'light';
  }, [media.matches]);

  // Calculate resolved theme
  const resolvedTheme: 'light' | 'dark' =
    theme === 'system' ? getSystemPreference() : theme;

  // Map to DaisyUI theme names and apply to document
  const DAISY_THEMES = { light: 'repro-light', dark: 'repro-dark' } as const;
  const applyTheme = useCallback((resolved: 'light' | 'dark') => {
    document.documentElement.dataset.theme = DAISY_THEMES[resolved];
    if (resolved === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // Load initial theme
  useEffect(() => {
    const loadInitialTheme = async () => {
      setLoading(true);

      // First, try to load from localStorage for immediate render
      const cachedTheme = localStorage.getItem(
        LOCAL_STORAGE_KEY,
      ) as ThemeMode | null;
      if (cachedTheme && ['system', 'light', 'dark'].includes(cachedTheme)) {
        setThemeState(cachedTheme);
        const resolved =
          cachedTheme === 'system' ? getSystemPreference() : cachedTheme;
        applyTheme(resolved);
      }

      // If authenticated and email is available, fetch from backend
      if (isAuthenticated && email) {
        try {
          const userSettings = await apiService.getUserSettings(email);
          if (userSettings && userSettings.themeMode) {
            const backendTheme = userSettings.themeMode;
            setThemeState(backendTheme);
            localStorage.setItem(LOCAL_STORAGE_KEY, backendTheme);
            const resolved =
              backendTheme === 'system' ? getSystemPreference() : backendTheme;
            applyTheme(resolved);
          } else {
            // If null/404, default to 'system'
            const defaultTheme: ThemeMode = 'system';
            setThemeState(defaultTheme);
            localStorage.setItem(LOCAL_STORAGE_KEY, defaultTheme);
            applyTheme(getSystemPreference());
          }
        } catch (error) {
          console.error('Error loading theme from backend:', error);
          // Fall back to cached theme or default
          const fallbackTheme = cachedTheme || 'system';
          setThemeState(fallbackTheme);
          localStorage.setItem(LOCAL_STORAGE_KEY, fallbackTheme);
          const resolved =
            fallbackTheme === 'system' ? getSystemPreference() : fallbackTheme;
          applyTheme(resolved);
        }
      } else {
        // Not authenticated, use cached theme or default to system
        const fallbackTheme = cachedTheme || 'system';
        setThemeState(fallbackTheme);
        localStorage.setItem(LOCAL_STORAGE_KEY, fallbackTheme);
        const resolved =
          fallbackTheme === 'system' ? getSystemPreference() : fallbackTheme;
        applyTheme(resolved);
      }

      setLoading(false);
    };

    loadInitialTheme();
  }, [isAuthenticated, email, getSystemPreference, applyTheme]);

  // Listen to system preference changes when theme is 'system'
  useEffect(() => {
    if (theme !== 'system') return;

    const handleChange = (e: MediaQueryListEvent) => {
      const newResolvedTheme = e.matches ? 'dark' : 'light';
      applyTheme(newResolvedTheme);
    };

    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, [theme, applyTheme, media]);

  // Apply theme when resolved theme changes
  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme, applyTheme]);

  // Set theme function
  const setTheme = useCallback(
    async (newTheme: ThemeMode) => {
      // Update state immediately
      setThemeState(newTheme);
      localStorage.setItem(LOCAL_STORAGE_KEY, newTheme);

      // Apply theme immediately
      const newResolvedTheme =
        newTheme === 'system' ? getSystemPreference() : newTheme;
      applyTheme(newResolvedTheme);

      // If authenticated, persist to backend
      if (isAuthenticated && email) {
        try {
          await apiService.updateUserSettings(email, { themeMode: newTheme });
        } catch (error) {
          console.error('Error persisting theme to backend:', error);
          // Theme is still applied locally, so this is not a critical error
        }
      }
    },
    [isAuthenticated, email, getSystemPreference, applyTheme],
  );

  return (
    <ThemeContext.Provider
      value={{
        theme,
        resolvedTheme,
        loading,
        setTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
